import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { reviewApi } from '@/services/api'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Alert from '@/components/ui/Alert'
import Modal from '@/components/ui/Modal'
import { formatDate } from '@/utils/format'
import { Star, MessageSquare, Search } from 'lucide-react'
import type { User, Barter } from '@/types'

interface ReviewSectionProps {
  type: 'user' | 'barter'
  id: number
  allowReview?: boolean
  barterOwner?: User | null
}

export default function ReviewSection({ type, id, allowReview = true, barterOwner = null }: ReviewSectionProps) {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [barterSearchQuery, setBarterSearchQuery] = useState('')
  const [userSearchResults, setUserSearchResults] = useState<User[]>([])
  const [barterSearchResults, setBarterSearchResults] = useState<Barter[]>([])
  const [selectedUser, setSelectedUser] = useState<User | null>(barterOwner)
  const [selectedBarter, setSelectedBarter] = useState<Barter | null>(null)
  const [formData, setFormData] = useState({
    reviewee_id: type === 'user' ? id.toString() : (barterOwner ? barterOwner.id.toString() : ''),
    barter_id: type === 'barter' ? id.toString() : '',
    rating: 5,
    review_text: '',
  })

  // Update form when barterOwner changes
  useEffect(() => {
    if (barterOwner && type === 'barter') {
      setSelectedUser(barterOwner)
      setFormData(prev => ({ ...prev, reviewee_id: barterOwner.id.toString() }))
    }
  }, [barterOwner, type])

  const { data } = useQuery({
    queryKey: ['reviews', type, id],
    queryFn: () =>
      type === 'user'
        ? reviewApi.list({ reviewee_id: id })
        : reviewApi.list({ barter_id: id }),
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => reviewApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews', type, id] })
      setShowModal(false)
      resetForm()
      setSuccess('Review submitted successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to submit review')
    },
  })

  const reviews = data?.data?.reviews || []

  // Search for users
  useEffect(() => {
    const searchUsers = async () => {
      if (userSearchQuery.length >= 2) {
        try {
          const response = await reviewApi.searchUsers(userSearchQuery)
          setUserSearchResults(response.data?.users || [])
        } catch (err) {
          console.error('User search failed:', err)
        }
      } else {
        setUserSearchResults([])
      }
    }

    const debounce = setTimeout(searchUsers, 300)
    return () => clearTimeout(debounce)
  }, [userSearchQuery])

  // Search for barters
  useEffect(() => {
    const searchBarters = async () => {
      if (barterSearchQuery.length >= 2) {
        try {
          const response = await reviewApi.searchBarters(barterSearchQuery)
          setBarterSearchResults(response.data?.barters || [])
        } catch (err) {
          console.error('Barter search failed:', err)
        }
      } else {
        setBarterSearchResults([])
      }
    }

    const debounce = setTimeout(searchBarters, 300)
    return () => clearTimeout(debounce)
  }, [barterSearchQuery])

  const handleSelectUser = (user: User) => {
    setSelectedUser(user)
    setFormData({ ...formData, reviewee_id: user.id.toString() })
    setUserSearchQuery('')
    setUserSearchResults([])
  }

  const handleSelectBarter = (barter: Barter) => {
    setSelectedBarter(barter)
    setFormData({ ...formData, barter_id: barter.id.toString() })
    setBarterSearchQuery('')
    setBarterSearchResults([])
  }

  const resetForm = () => {
    setFormData({
      reviewee_id: type === 'user' ? id.toString() : '',
      barter_id: type === 'barter' ? id.toString() : '',
      rating: 5,
      review_text: '',
    })
    setSelectedUser(null)
    setSelectedBarter(null)
    setUserSearchQuery('')
    setBarterSearchQuery('')
    setUserSearchResults([])
    setBarterSearchResults([])
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.reviewee_id) {
      setError('Please select a user to review')
      return
    }

    createMutation.mutate({
      reviewee_id: parseInt(formData.reviewee_id),
      barter_id: formData.barter_id ? parseInt(formData.barter_id) : null,
      rating: formData.rating,
      review_text: formData.review_text || null,
    })
  }

  const renderStars = (rating: number, interactive = false, onSelect?: (rating: number) => void) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            onClick={() => interactive && onSelect && onSelect(star)}
            className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}
            disabled={!interactive}
          >
            <Star
              className={`w-5 h-5 ${
                star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    )
  }

  const calculateAverageRating = () => {
    if (reviews.length === 0) return '0'
    const sum = reviews.reduce((acc: number, review: any) => acc + review.rating, 0)
    return (sum / reviews.length).toFixed(1)
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Reviews</h2>
          {reviews.length > 0 && (
            <div className="flex items-center space-x-2 mt-1">
              {renderStars(Math.round(parseFloat(calculateAverageRating())))}
              <span className="text-sm text-gray-600">
                {calculateAverageRating()} out of 5 ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
              </span>
            </div>
          )}
        </div>
        {allowReview && (
          <Button onClick={() => setShowModal(true)} size="sm">
            <MessageSquare className="w-4 h-4 mr-2" />
            Write Review
          </Button>
        )}
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Reviews List */}
      <div className="space-y-3">
        {reviews.map((review: any) => (
          <Card key={review.id}>
            <div className="space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900">
                      {review.reviewer_first_name} {review.reviewer_last_name}
                    </h3>
                    {type === 'user' && (
                      <>
                        <span className="text-gray-400">→</span>
                        <h3 className="font-semibold text-gray-900">
                          {review.reviewee_first_name} {review.reviewee_last_name}
                        </h3>
                      </>
                    )}
                  </div>
                  {review.barter_title && (
                    <p className="text-sm text-gray-500 mt-1">Barter: {review.barter_title}</p>
                  )}
                </div>
                <div className="text-right">
                  {renderStars(review.rating)}
                  <p className="text-xs text-gray-500 mt-1">{formatDate(review.created_at)}</p>
                </div>
              </div>

              {review.review_text && (
                <div className="bg-gray-50 p-3 rounded-lg mt-2">
                  <p className="text-gray-700 text-sm">{review.review_text}</p>
                </div>
              )}
            </div>
          </Card>
        ))}

        {reviews.length === 0 && (
          <Card>
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No reviews yet</p>
            </div>
          </Card>
        )}
      </div>

      {/* Create Review Modal */}
      {showModal && (
        <Modal title="Write a Review" onClose={() => { setShowModal(false); resetForm(); }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* User Search (only if not reviewing a specific user) */}
            {type !== 'user' && (
              <div>
                <label className="label">Search for User to Review *</label>
                {selectedUser ? (
                  <div className="flex items-center justify-between bg-primary-50 border border-primary-200 rounded-lg p-3">
                    <div>
                      <p className="font-medium text-gray-900">
                        {selectedUser.first_name} {selectedUser.last_name}
                      </p>
                      <p className="text-sm text-gray-600">{selectedUser.email}</p>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSelectedUser(null)
                        setFormData({ ...formData, reviewee_id: '' })
                      }}
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        className="input pl-10"
                        placeholder="Search by name or email..."
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                      />
                    </div>
                    {userSearchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {userSearchResults.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                            onClick={() => handleSelectUser(user)}
                          >
                            <p className="font-medium text-gray-900">
                              {user.first_name} {user.last_name}
                            </p>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            {user.profession && (
                              <p className="text-xs text-gray-500">{user.profession}</p>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Barter Search (only if not reviewing a specific barter) */}
            {type !== 'barter' && (
              <div>
                <label className="label">Related Barter (Optional)</label>
                {selectedBarter ? (
                  <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div>
                      <p className="font-medium text-gray-900">{selectedBarter.title}</p>
                      <p className="text-sm text-gray-600">{selectedBarter.type}</p>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSelectedBarter(null)
                        setFormData({ ...formData, barter_id: '' })
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        className="input pl-10"
                        placeholder="Search barters by title..."
                        value={barterSearchQuery}
                        onChange={(e) => setBarterSearchQuery(e.target.value)}
                      />
                    </div>
                    {barterSearchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {barterSearchResults.map((barter) => (
                          <button
                            key={barter.id}
                            type="button"
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                            onClick={() => handleSelectBarter(barter)}
                          >
                            <p className="font-medium text-gray-900">{barter.title}</p>
                            <p className="text-sm text-gray-600">
                              Type: {barter.type} • Status: {barter.status}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="label">Rating *</label>
              <div className="mt-2">
                {renderStars(formData.rating, true, (rating) =>
                  setFormData({ ...formData, rating })
                )}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {formData.rating} out of 5 stars
              </p>
            </div>

            <div>
              <label className="label">Review (Optional)</label>
              <textarea
                className="textarea"
                rows={4}
                placeholder="Share your experience..."
                value={formData.review_text}
                onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
              />
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" isLoading={createMutation.isPending}>
                Submit Review
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}
